import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import {
  commissionsApi,
  type CommissionClaimDetailDto,
  type PaymentMethod,
} from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { currency } from "@/components/commissions/formatters";
import { calendarDateInputToIso, isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import { WHOLE_CENTS_MESSAGE, hasFractionOfACent, toCents } from "./wholeCents";

interface Props {
  open: boolean;
  onClose: () => void;
  claim: CommissionClaimDetailDto;
}

const METHODS: PaymentMethod[] = ["Check", "ACH", "Wire", "Payroll", "Other"];

export function RecordPaymentModal({ open, onClose, claim }: Props) {
  const qc = useQueryClient();
  const remaining = claim.summary.balanceRemaining;
  const tz = useUserTimeZone();
  const [date, setDate] = useState(() => isoDateInTz(new Date(), tz));
  const [amount, setAmount] = useState<string>(remaining.toFixed(2));
  const [method, setMethod] = useState<PaymentMethod>("ACH");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const amountNum = Number(amount) || 0;
  // Compared in whole cents. The old `amountNum > remaining + 0.001` let an
  // amount up to a tenth of a cent over the balance through to the server,
  // which refused it as "Payment of $10.00 exceeds remaining balance $10.00".
  // A fraction of a cent is refused here now, in the server's own words.
  const subCent = hasFractionOfACent(amountNum);
  const tooLarge = !subCent && toCents(amountNum) > toCents(remaining);
  const amountError = subCent
    ? WHOLE_CENTS_MESSAGE
    : tooLarge
      ? "Exceeds remaining balance"
      : undefined;
  const canSubmit =
    date !== "" && amountNum > 0 && amountError === undefined && reference.trim().length > 0;

  // 🔴 NOON UTC, NOT MIDNIGHT. `new Date(date).toISOString()` stored this
  // wall-calendar day at UTC midnight, which every tz-aware renderer then drew
  // as the PREVIOUS day for any reader west of UTC. This file held one of the
  // eight `BLOCKED_BASELINE` entries in `calendarDateWrites`, and the block was
  // real: `AdminCommissionService`'s old `<= dt` upper bound would have EXCLUDED
  // a noon value on the range's own last day. `CRIT-29` made that predicate
  // half-open (`p.PaymentDate < dayAfter`, `AdminCommissionService:472`), so the
  // blocker is gone and the entry is DELETED rather than decremented.
  //
  // The guard is not dead code. Neither date input is `required` and `canSubmit`
  // did not consult the date, so an emptied field reached `new Date("")` and
  // threw `RangeError: Invalid time value` from inside the mutation. `canSubmit`
  // now disables the button, and this fails with a sentence if anything else
  // ever reaches it.
  const mutation = useMutation({
    mutationFn: () => {
      const paymentDate = calendarDateInputToIso(date);
      if (!paymentDate) throw new Error("Enter a payment date.");
      return commissionsApi.recordPayment(claim.summary.id, {
        paymentDate,
        amount: amountNum,
        paymentMethod: method,
        referenceNumber: reference.trim(),
        notes: notes.trim() || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.all });
      // WS7 — the admin payment ledger + summary tiles live under a
      // separate prefix root ("admin"), so commissionKeys.all does not
      // reach them; without this the YTD/MTD/Filtered tiles serve
      // stale-0 from cache after a new payment is recorded.
      qc.invalidateQueries({ queryKey: commissionKeys.adminPayments() });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Record payment — ${claim.summary.claimNumber}`}
      description={`Remaining balance: ${currency(remaining)}. Payment cannot exceed the remaining balance.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            Record payment
          </Button>
        </>
      }
    >
      {error && (
        <div
          role="alert"
          className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small mb-space-3"
        >
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-space-3">
        <FormField label="Payment date" required>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </FormField>
        <FormField label="Amount" required error={amountError}>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            invalid={amountError !== undefined}
          />
        </FormField>
        <FormField label="Method" required>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            className="w-full h-10 px-space-3 border border-border-light rounded-md font-body text-small bg-white-pure"
          >
            {METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Reference number" required helperText="Check #, ACH ID, wire confirmation, etc.">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </FormField>
      </div>
      <FormField label="Notes (optional)" className="mt-space-3">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
        />
      </FormField>
    </Modal>
  );
}
